/* =========================================================================
 * Tiger Flags — Mobile size-pill collapse ("Ver mais tamanhos")
 *
 * Progressive enhancement only. Touches NO Shopify variant logic:
 * radios/labels stay native and in the product form. On mobile, when a
 * pill option has more than 9 values, the grid is clipped to ~3 rows and
 * a "Ver mais tamanhos" button reveals the rest with a smooth animation.
 *
 * Desktop (>=750px) is untouched. Re-initialises itself when Dawn swaps
 * <variant-selects> on variant change (MutationObserver).
 * ========================================================================= */
(function () {
  'use strict';

  // Scoped to the PDP via the <product-info> custom element (this theme's
  // body has no template-product class, so we anchor on real DOM instead).
  if (!document.querySelector('product-info')) return;

  var MOBILE = window.matchMedia('(max-width: 749px)');
  var GRID_MIN = 6; // dense option (Tamanho-like) → compact 3-col grid
  var VISIBLE_COUNT = 9; // 3x3 grid before "ver mais"
  var MIN_HIDDEN = 3; // don't collapse to hide only 1-2 pills (10-11 = show all)

  function labelOf(radio) {
    return (radio.labels && radio.labels[0]) || radio.nextElementSibling;
  }

  // Wrap just the pills (everything except the <legend>) in a div so the
  // grid/clip lives on .vh-pill-track and the legend is never a grid item
  // nor clipped. Radios keep their form="" attr and labels their for="",
  // so Shopify variant logic is untouched. Idempotent.
  function getTrack(fieldset) {
    var track = fieldset.querySelector(':scope > .vh-pill-track');
    if (track) return track;
    track = document.createElement('div');
    track.className = 'vh-pill-track';
    var legend = fieldset.querySelector('legend');
    var nodes = Array.prototype.slice.call(fieldset.childNodes);
    nodes.forEach(function (n) {
      if (n !== legend) track.appendChild(n);
    });
    fieldset.appendChild(track);
    return track;
  }

  // Remove only the collapse layer (button + clamp), keep grid + wrapper.
  function uncollapse(fieldset) {
    fieldset.classList.remove('vh-pill-collapsible');
    var track = fieldset.querySelector(':scope > .vh-pill-track');
    if (track) {
      track.classList.remove('is-collapsed');
      track.style.maxHeight = '';
    }
    var btn = fieldset.nextElementSibling;
    if (btn && btn.classList.contains('vh-sizes-more')) btn.remove();
  }

  // Full reset of our visual layer (wrapper is left in place, inert via
  // display:contents, so re-scans stay cheap and idempotent).
  function teardown(fieldset) {
    uncollapse(fieldset);
    fieldset.classList.remove('vh-pill-grid');
  }

  function collapsedHeight(track, radios) {
    // Bottom edge of the 9th pill, relative to the track box.
    var ref = labelOf(radios[VISIBLE_COUNT - 1]);
    if (!ref) return track.scrollHeight;
    var tb = track.getBoundingClientRect();
    var rb = ref.getBoundingClientRect();
    return Math.ceil(rb.bottom - tb.top) + 4;
  }

  function setup(fieldset) {
    var radios = fieldset.querySelectorAll('input[type="radio"]');

    // Desktop: nothing applies — keep the original layout.
    if (!MOBILE.matches) {
      teardown(fieldset);
      return;
    }

    // Dense options (many values, e.g. Tamanho) → compact 3-col grid.
    // Few-but-long options (Impressão, Acabamento) stay natural so their
    // labels wrap instead of being clipped.
    if (radios.length < GRID_MIN) {
      teardown(fieldset);
      return;
    }
    fieldset.classList.add('vh-pill-grid');
    var track = getTrack(fieldset);

    // Collapse ("ver mais") only when it actually buys space (>= 3 hidden).
    if (radios.length < VISIBLE_COUNT + MIN_HIDDEN) {
      uncollapse(fieldset);
      return;
    }
    if (fieldset.classList.contains('vh-pill-collapsible')) return;

    fieldset.classList.add('vh-pill-collapsible');

    // If the selected pill is hidden, start expanded so it stays visible.
    var checkedIdx = -1;
    radios.forEach(function (r, i) {
      if (r.checked) checkedIdx = i;
    });
    var startCollapsed = checkedIdx < VISIBLE_COUNT;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'vh-sizes-more';
    btn.setAttribute('aria-controls', fieldset.id || '');
    fieldset.after(btn);

    function render(collapsed) {
      if (collapsed) {
        track.style.maxHeight = collapsedHeight(track, radios) + 'px';
        track.classList.add('is-collapsed');
        btn.setAttribute('aria-expanded', 'false');
        var hidden = radios.length - VISIBLE_COUNT;
        btn.textContent = 'Ver mais tamanhos (+' + hidden + ')';
      } else {
        track.style.maxHeight = track.scrollHeight + 'px';
        track.classList.remove('is-collapsed');
        btn.setAttribute('aria-expanded', 'true');
        btn.textContent = 'Ver menos';
        // Release the cap after the transition so layout can grow freely.
        window.setTimeout(function () {
          if (!track.classList.contains('is-collapsed')) {
            track.style.maxHeight = 'none';
          }
        }, 420);
      }
    }

    btn.addEventListener('click', function () {
      var collapsing = !track.classList.contains('is-collapsed');
      if (!collapsing) {
        render(false);
        return;
      }
      // Animate from current full height down to the clipped height.
      track.style.maxHeight = track.scrollHeight + 'px';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          render(true);
        });
      });
    });

    render(startCollapsed);
  }

  function scan(root) {
    (root || document)
      .querySelectorAll('.product-form__input--pill')
      .forEach(setup);
  }

  // Observe the STABLE <product-info> element, not <variant-selects>.
  // On variant change Dawn's HTMLUpdateUtility.viewTransition() REPLACES the
  // whole <variant-selects> element (inserts a fresh one, removes the old),
  // so an observer bound to the old <variant-selects> is orphaned and never
  // sees the new picker — the grid/track enhancement was silently lost and
  // the pills fell back to the default flex-wrap layout. <product-info>
  // persists across that swap, so observing it catches the new markup.
  function observe(root) {
    if (root.__vhCollapseObserved) return;
    root.__vhCollapseObserved = true;
    // True only when Dawn actually swapped the picker markup — i.e. a
    // pill fieldset or a radio was added/removed. Our own writes (button
    // text node, wrapper move, track class/style) must NOT trigger this,
    // otherwise clicking "Ver mais" would tear itself down.
    function isDawnSwap(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        var nodes = [].slice
          .call(m.addedNodes)
          .concat([].slice.call(m.removedNodes));
        for (var j = 0; j < nodes.length; j++) {
          var n = nodes[j];
          if (n.nodeType !== 1) continue; // skip text nodes (button label)
          if (n.classList && n.classList.contains('vh-sizes-more')) continue;
          if (n.classList && n.classList.contains('vh-pill-track')) continue;
          if (
            n.matches &&
            (n.matches('.product-form__input--pill, input[type="radio"]') ||
              n.querySelector(
                '.product-form__input--pill, input[type="radio"]'
              ))
          ) {
            return true;
          }
        }
      }
      return false;
    }

    var mo = new MutationObserver(function (mutations) {
      if (root.__vhBusy) return;
      if (!isDawnSwap(mutations)) return;
      root.__vhBusy = true;
      mo.disconnect();
      root.querySelectorAll('.product-form__input--pill').forEach(teardown);
      requestAnimationFrame(function () {
        scan(root);
        mo.observe(root, { childList: true, subtree: true });
        root.__vhBusy = false;
      });
    });
    mo.observe(root, { childList: true, subtree: true });
  }

  function init() {
    document.querySelectorAll('product-info').forEach(function (pi) {
      scan(pi);
      observe(pi);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Safety net: pill markup / fonts can settle after first paint.
  window.addEventListener('load', init);
  // Theme editor / dynamic section reload.
  document.addEventListener('shopify:section:load', function () {
    requestAnimationFrame(init);
  });

  // Breakpoint change: enable on mobile, fully restore on desktop.
  var onBP = function () {
    document
      .querySelectorAll('product-info .product-form__input--pill')
      .forEach(teardown);
    requestAnimationFrame(init);
  };
  if (MOBILE.addEventListener) MOBILE.addEventListener('change', onBP);
  else if (MOBILE.addListener) MOBILE.addListener(onBP);
})();
